import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { createContext, forwardRef, useContext, useEffect, useState } from 'react'
import { cn } from '../lib/cn.js'
import { useIsField } from '../lib/media.js'
import { Icon, type IconName } from './Icon.js'
import { SectionOverlay, Spinner } from './Loader.js'

export { Spinner }

/**
 * The interface kit.
 *
 * Density is not decided here. Every control sizes itself from the
 * `--control-*` and `--row-pad-*` tokens declared in `styles.css`, which are
 * condensed on a pointer and back at the 44px touch floor below 1024px. That
 * means this file contains no breakpoint: the office screen is dense and the
 * phone in the van is tappable, from one set of class names.
 *
 * The look is the job card's, carried through the app (decision 102): a
 * Charcoal command bar on top of a screen, Inter headings, and Eco Green for
 * the one thing to do — the primary action, the open tab, the current stage.
 *
 * Two colour rules, both easy to break by accident:
 *
 * - Eco Green means "do this", always as a fill with Charcoal text on it.
 *   Charcoal (ink-900) means "this is on". A selected filter chip or a
 *   current nav item is Charcoal, never green.
 * - Status tones come from the lookup maps at the bottom. Never pick one by
 *   eye, and never invent a seventh.
 *
 * Composition goes through `cn`, never bare `clsx`: a caller's `className` has
 * to be able to override the base one, and concatenation leaves that to CSS
 * source order.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

/**
 * Primary is the brand pack's button: Eco Green with Charcoal text (8.0:1),
 * the same on a page, in a dialog and on a command bar.
 */
const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-brand-500 font-semibold text-ink-900 hover:bg-brand-550 active:bg-brand-550',
  secondary: 'bg-white text-ink-800 ring-1 ring-inset ring-ink-300 hover:bg-ink-50',
  ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

/** The job card header's chips: Eco Green leads, the rest are white on Charcoal. */
const charcoalButtonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-brand-500 font-bold text-ink-900 hover:bg-brand-400 active:bg-brand-400',
  secondary: 'bg-white/[0.08] font-semibold text-white hover:bg-white/15',
  ghost: 'text-white/80 hover:bg-white/10 hover:text-white',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

/**
 * Whether a control is drawn on a `CommandBar`'s Charcoal. The kit's own
 * controls read it; anything hand-drawn inside a bar asks `useOnCharcoal`.
 */
const CharcoalSurface = createContext(false)

export function useOnCharcoal(): boolean {
  return useContext(CharcoalSurface)
}

const buttonSizes: Record<Size, string> = {
  sm: 'min-h-[var(--control-sm)] px-[var(--control-pad-sm)] text-xs gap-1.5',
  md: 'min-h-[var(--control-md)] px-[var(--control-pad-md)] text-sm gap-1.5',
  lg: 'min-h-[var(--control-lg)] px-[var(--control-pad-lg)] rounded-lg text-sm gap-2',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  block?: boolean
}

/**
 * The button skin on its own, for the handful of places where the control has
 * to be an anchor: `tel:` on the field screens, and router links that must
 * still middle-click to a new tab. A `<button>` with an onClick navigate is
 * not a link and behaves wrong under every modifier key.
 */
export function buttonClass({
  variant = 'secondary',
  size = 'md',
  block,
  onCharcoal = false,
  className,
}: {
  variant?: ButtonVariant
  size?: Size
  block?: boolean
  /** Drawn on a command bar. The kit's `Button` reads this from context. */
  onCharcoal?: boolean
  className?: string
} = {}): string {
  return cn(
    'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-50',
    (onCharcoal ? charcoalButtonVariants : buttonVariants)[variant],
    buttonSizes[size],
    block && 'w-full',
    className,
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, block, className, children, disabled, ...props },
  ref,
) {
  const onCharcoal = useOnCharcoal()
  return (
    <button
      ref={ref}
      // A button mid-submit stays disabled, so a double tap on a slow
      // connection cannot raise two invoices.
      disabled={disabled || loading}
      className={buttonClass({ variant, size, block, onCharcoal, className })}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  )
})

export interface FieldProps {
  label: string
  hint?: string
  error?: string | string[]
  required?: boolean
  children: ReactNode
  htmlFor?: string
}

export function Field({ label, hint, error, required, children, htmlFor }: FieldProps) {
  const message = Array.isArray(error) ? error[0] : error

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-700">
        {label}
        {required ? <span className="ml-0.5 text-red-600">*</span> : null}
      </label>
      {children}
      {/* The error replaces the hint rather than stacking, so the field does
          not reflow and push the submit button under the thumb. */}
      {message ? (
        <p className="text-xs text-red-600" role="alert">
          {message}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-500">{hint}</p>
      ) : null}
    </div>
  )
}

const controlStyles =
  'block w-full rounded-md border-0 bg-white px-2.5 text-sm text-ink-900 ring-1 ring-inset ' +
  'ring-ink-300 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-inset ' +
  'focus:ring-brand-600 disabled:bg-ink-100 disabled:text-ink-500 min-h-[var(--control-md)]'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(controlStyles, className)} {...props} />
  },
)

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(controlStyles, 'pr-8', className)} {...props}>
        {children}
      </select>
    )
  },
)

export function Textarea({
  className,
  ...props
}: React.ComponentProps<'textarea'>) {
  return <textarea className={cn(controlStyles, 'min-h-18 py-2', className)} {...props} />
}

/**
 * A card is white, a hairline ring and no shadow. A dense screen of shadowed
 * cards reads as noise.
 *
 * Every card is the dispatch board's card — 14px radius and an inset ring,
 * from the `.board-card` rule in `styles.css` — because every screen now sits
 * on the same ink-100 field. `variant` survives for callers that still say
 * `board`; the two used to differ and no longer do.
 */
export function Card({
  variant = 'default',
  className,
  children,
}: {
  variant?: 'default' | 'board'
  className?: string
  children: ReactNode
}) {
  return (
    <div
      data-variant={variant}
      className={cn('board-card overflow-hidden bg-white', className)}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  action,
  description,
}: {
  title: ReactNode
  action?: ReactNode
  description?: ReactNode
}) {
  // The job card's section heading (`Label` in `job-card/panes.tsx`).
  const heading = (
    <h2 className="font-display text-[15px] leading-snug font-extrabold text-ink-900">{title}</h2>
  )

  return (
    <div className="flex min-h-[var(--card-head)] items-center justify-between gap-2 border-b border-ink-100 px-[var(--card-pad)] py-1">
      {description ? (
        <div className="min-w-0">
          {heading}
          <p className="mt-0.5 text-xs text-ink-500">{description}</p>
        </div>
      ) : (
        heading
      )}
      {action}
    </div>
  )
}

/**
 * Five tones, all inside the brand (decision 110). Pick by meaning, never by
 * eye, and never add a sixth: the brand has two colours and one red.
 *
 * - `neutral`: grey. Nothing to do yet, or nothing to do any more: a draft,
 *   pencilled in, void, archived, imported.
 * - `outline`: white with a Charcoal edge. Out with somebody and nothing
 *   wrong: sent, issued, booked, invited, you.
 * - `green`: Green Tint. Settled and good: paid, accepted, confirmed, done.
 * - `dark`: Charcoal. Look at this one: live, high priority, part paid,
 *   opened by the customer, due soon.
 * - `red`: the one alarm. Overdue, declined, emergency, a hazard, cancelled.
 */
export type Tone = 'neutral' | 'outline' | 'green' | 'dark' | 'red'

/** Exported so anything drawing its own tinted chip agrees with the badges. */
export const toneStyles: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700 ring-ink-200',
  outline: 'bg-white text-ink-900 ring-ink-400',
  green: 'bg-brand-100 text-brand-800 ring-brand-200',
  dark: 'bg-ink-900 text-white ring-ink-900',
  red: 'bg-red-50 text-red-700 ring-red-200',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-1.5 py-px text-[11px] font-medium ring-1 ring-inset whitespace-nowrap',
        toneStyles[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * The pill toggle. Written inline and identically in the jobs and inbox
 * filters before this existed, so the two could drift.
 *
 * Selected is Charcoal on white, not green: green means "do this". On a
 * command bar it turns over, white with Charcoal text.
 */
export function FilterChip({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  className?: string
}) {
  const onCharcoal = useOnCharcoal()
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-[var(--control-sm)] items-center rounded-md px-2 text-[11px] font-medium transition-colors',
        onCharcoal
          ? active
            ? 'bg-white font-semibold text-ink-900'
            : 'bg-white/[0.08] text-white hover:bg-white/15'
          : active
            ? 'bg-ink-900 font-semibold text-white ring-1 ring-inset ring-ink-900'
            : 'bg-white text-ink-600 ring-1 ring-inset ring-ink-300 hover:bg-ink-50',
        className,
      )}
    >
      {children}
    </button>
  )
}

/**
 * An on/off switch, for a setting that takes effect the moment it moves.
 *
 * `role="switch"` and not a checkbox: there is no form under it and nothing to
 * submit, so "on / off" is the honest reading rather than "ticked". Use it when
 * a thing is either happening or not. Where the choice is between two named
 * alternatives that are both something — status or category, day or timeline —
 * a segmented control still says more than a switch called "Category".
 *
 * On is **Charcoal** (ink-900), per the colour rule at the top of this file. A switch is
 * reporting a state, not offering an action, so it never goes green.
 *
 * The track is 24px, which is under the touch floor, so the button around it
 * takes `--control-sm` and the extra height is hit area. `label` is the
 * accessible name — draw the visible text beside it, and the two agreeing is
 * the point.
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  className,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
  /** `sm` is a 16px track for a toolbar, beside 12px text. */
  size?: 'md' | 'sm'
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex min-h-[var(--control-sm)] shrink-0 items-center',
        disabled ? 'cursor-default' : 'cursor-pointer',
        className,
      )}
    >
      <SwitchTrack checked={checked} disabled={disabled} size={size} />
    </button>
  )
}

/** The drawn half of `Toggle`, for a row that is itself the switch. */
export function SwitchTrack({
  checked,
  disabled = false,
  size = 'md',
}: {
  checked: boolean
  disabled?: boolean
  size?: 'md' | 'sm'
}) {
  const small = size === 'sm'
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative shrink-0 rounded-[5px] transition-colors',
        small ? 'h-4 w-7' : 'h-6 w-10',
        checked ? 'bg-ink-900' : 'bg-ink-300',
        disabled ? 'opacity-50' : checked ? 'hover:bg-ink-700' : 'hover:bg-ink-400',
      )}
    >
      <span
        className={cn(
          'absolute rounded-[3px] bg-white shadow-sm transition-transform',
          small ? 'top-[2px] left-[2px] size-3' : 'top-[3px] left-[3px] size-[18px]',
          checked && (small ? 'translate-x-3' : 'translate-x-4'),
        )}
      />
    </span>
  )
}


/**
 * Status tones are defined once, so a status never means two things.
 *
 * The ServiceM8 colours (Quote orange, Work Order blue) are gone with the
 * rest of the rainbow (decision 110): a stage is a word, and the tone only
 * says whether it is still open, settled or lost.
 */
export const JOB_STATUS_TONE: Record<string, Tone> = {
  LEAD: 'neutral',
  QUOTING: 'outline',
  QUOTED: 'outline',
  APPROVED: 'outline',
  SCHEDULED: 'outline',
  IN_PROGRESS: 'dark',
  ON_HOLD: 'neutral',
  AWAITING_PARTS: 'neutral',
  COMPLETED: 'green',
  CANCELLED: 'red',
}

/** Hex for anything that cannot use a tone class: the brand's own values. */
export const JOB_STATUS_COLOUR: Record<string, string> = {
  LEAD: '#9ca49f',
  QUOTING: '#4f5652',
  QUOTED: '#4f5652',
  APPROVED: '#1e2320',
  SCHEDULED: '#1e2320',
  IN_PROGRESS: '#1e2320',
  ON_HOLD: '#9ca49f',
  AWAITING_PARTS: '#9ca49f',
  COMPLETED: '#94c83d',
  CANCELLED: '#b91c1c',
}

/**
 * How a job status reads to a person.
 *
 * The stage words the job card and the Jobs list use (decision 77): Quote,
 * Work Order, Complete, Unsuccessful. Approved, Scheduled and In Progress all
 * read "Work Order" — they are the same ServiceM8 Work Order colour on the
 * board, and calling the board's copy "In Progress" gave one job two names.
 * Decision 103.
 */
export function jobStatusLabel(status: string): string {
  switch (status) {
    case 'QUOTING':
    case 'QUOTED':
      return 'Quote'
    case 'APPROVED':
    case 'SCHEDULED':
    case 'IN_PROGRESS':
      return 'Work Order'
    case 'COMPLETED':
      return 'Complete'
    case 'CANCELLED':
      return 'Unsuccessful'
    default:
      return humanise(status)
  }
}

/**
 * Labels for filters and status pickers, where Approved / Scheduled /
 * In Progress must stay distinguishable even though the board collapses them.
 */
export function jobStatusOptionLabel(status: string): string {
  switch (status) {
    case 'QUOTING':
      return 'Quote'
    case 'APPROVED':
      return 'Work Order'
    case 'COMPLETED':
      return 'Complete'
    case 'CANCELLED':
      return 'Unsuccessful'
    default:
      return humanise(status)
  }
}

export const INVOICE_STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  ISSUED: 'outline',
  PARTIALLY_PAID: 'dark',
  PAID: 'green',
  OVERDUE: 'red',
  VOID: 'neutral',
  WRITTEN_OFF: 'neutral',
}

export const QUOTE_STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  SENT: 'outline',
  VIEWED: 'dark',
  ACCEPTED: 'green',
  DECLINED: 'red',
  EXPIRED: 'neutral',
  SUPERSEDED: 'neutral',
}

/** The board's frames in words (decision 105): pencilled grey, confirmed green, live Charcoal. */
export const VISIT_STATUS_TONE: Record<string, Tone> = {
  PROPOSED: 'neutral',
  CONFIRMED: 'green',
  EN_ROUTE: 'dark',
  ON_SITE: 'dark',
  COMPLETED: 'green',
  CANCELLED: 'red',
  NO_SHOW: 'red',
}

export const PRIORITY_TONE: Record<string, Tone> = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'dark',
  EMERGENCY: 'red',
}

/** Turns SCREAMING_SNAKE into something a person would say. */
export function humanise(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description?: string
  action?: ReactNode
  /** A glyph from the icon set, drawn in a soft tile above the title. */
  icon?: IconName
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-6 py-6 text-center">
      {icon ? (
        <span className="mb-2 grid size-10 place-items-center rounded-lg bg-brand-100 text-brand-700">
          <Icon name={icon} />
        </span>
      ) : null}
      <p className="font-display text-[15px] font-extrabold text-ink-900">{title}</p>
      {description ? <p className="max-w-[340px] text-xs text-ink-500">{description}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-6 text-center">
      <p className="text-sm font-medium text-red-700">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}

/**
 * A skeleton rather than a spinner for lists.
 *
 * A spinner tells you to wait; a skeleton tells you what is coming and stops
 * the page jumping when it arrives.
 */
export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="relative divide-y divide-ink-100" aria-hidden="true">
      {/* The rows say what is coming; the mark says it is on its way, once
          the wait is long enough to notice. Decision 116. */}
      <SectionOverlay />
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
        >
          <div className="h-3 w-16 animate-pulse rounded-sm bg-ink-200" />
          <div className="h-3 flex-1 animate-pulse rounded-sm bg-ink-100" />
          <div className="h-3 w-24 animate-pulse rounded-sm bg-ink-100" />
        </div>
      ))}
    </div>
  )
}

export function Avatar({
  name,
  colour,
  size = 'md',
  className,
}: {
  name: string
  colour?: string
  size?: Size
  className?: string
}) {
  const dimensions = {
    sm: 'size-5 text-[9px]',
    md: 'size-7 text-[10px]',
    lg: 'size-9 text-xs',
  }[size]

  // Initials on a neutral square, whatever the person's colour: the brand is
  // two colours, and a rainbow of circles was what read as off brand
  // (decision 105). Callers still pass it; only the map's routes draw it.
  void colour
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md bg-ink-100 font-semibold text-ink-800 ring-1 ring-ink-200 ring-inset',
        dimensions,
        className,
      )}
      title={name}
    >
      {initialsOf(name)}
    </span>
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

/**
 * Adjacent controls sharing one outline.
 *
 * Filter, sort and group are one decision — how this list reads — so they are
 * one object on the toolbar rather than three loose buttons competing with the
 * search box and the primary action for attention.
 */
export function ButtonGroup({
  children,
  className,
  label,
}: {
  children: ReactNode
  className?: string
  label?: string
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'inline-flex divide-x divide-ink-300 overflow-hidden rounded-md ring-1 ring-inset ring-ink-300',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The toolbar's search box.
 *
 * Debounced by 250ms and echoing whatever it is given, so the URL can own the
 * term without a keystroke costing a request. Lives here rather than beside the
 * first list that needed one, because three toolbars now use it.
 */
export function SearchBox({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}) {
  const [draft, setDraft] = useState(value)

  // Keeps the box in step when the term changes from elsewhere — the back
  // button, or a shared link opened in place.
  useEffect(() => setDraft(value), [value])

  useEffect(() => {
    if (draft === value) return
    const timer = setTimeout(() => onChange(draft), 250)
    return () => clearTimeout(timer)
  }, [draft, value, onChange])

  return (
    <div className={cn('relative w-full sm:w-64', className)}>
      <Icon
        name="search"
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-ink-400"
      />
      <Input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg pl-8"
        aria-label={placeholder ?? 'Search'}
      />
    </div>
  )
}

/**
 * The filter strip above a list. It carries a bottom hairline because it
 * normally sits at the top of a card with rows under it; a toolbar that is a
 * card of its own passes `border-b-0`.
 */
export function Toolbar({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 border-b border-ink-200 bg-white px-[var(--row-pad-x)] py-2',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The job card's header, for a screen: Charcoal at a desk, with the screen's
 * name and the actions that belong to all of it. The kit's controls inside
 * turn over to the card header's chips: the lead action is Eco Green and the
 * rest are white on Charcoal. A hand-drawn control asks `useOnCharcoal`.
 *
 * The field app draws no bar. Its shell already says where you are, and what
 * is left sits on the page as it did. Decisions 93 and 102.
 */
export function CommandBar({ className, children }: { className?: string; children: ReactNode }) {
  const field = useIsField()
  return (
    <CharcoalSurface.Provider value={!field}>
      <div
        data-command-bar={field ? undefined : ''}
        className={cn(
          'flex flex-wrap items-center justify-between gap-x-4 gap-y-2',
          !field && 'min-h-14 rounded-[14px] bg-ink-900 px-4 py-2 text-white',
          className,
        )}
      >
        {children}
      </div>
    </CharcoalSurface.Provider>
  )
}

/** A command bar's title, the job number's Nunito ExtraBold. */
export function CommandTitle({ className, children }: { className?: string; children: ReactNode }) {
  const onCharcoal = useOnCharcoal()
  return (
    <h1
      className={cn(
        onCharcoal
          ? 'font-display text-[20px] leading-tight font-extrabold text-white'
          : 'font-display text-lg font-extrabold text-ink-900',
        className,
      )}
    >
      {children}
    </h1>
  )
}

/** The line under a command bar's title, in Green 300 like the job's client · suburb. */
export function CommandMeta({ className, children }: { className?: string; children: ReactNode }) {
  const onCharcoal = useOnCharcoal()
  return (
    <div
      className={cn(
        onCharcoal ? 'text-[13px] font-medium text-brand-300' : 'text-xs text-ink-500',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** A screen's title and its one action, for a screen with nothing else to say. */
export function PageTitle({
  children,
  action,
  description,
}: {
  children: ReactNode
  action?: ReactNode
  description?: ReactNode
}) {
  return (
    <CommandBar>
      <div className="min-w-0">
        <CommandTitle>{children}</CommandTitle>
        {description ? <CommandMeta>{description}</CommandMeta> : null}
      </div>
      {action}
    </CommandBar>
  )
}

/**
 * The page column. Letterbox screens share one width: 1440px, the same cap
 * the Jobs list and Settings already used. Narrower sizes stay on the type
 * for a screen that truly cannot use the room (none do today).
 *
 * `field` puts the column on the board's `ink-100` page instead of the shell's
 * `ink-50`. That is the whole difference between a screen that reads as part of
 * the dispatch board and one that reads as a different product: the icon rail
 * is `ink-100`, so a white card wants the same field beside it.
 */
export function Page({
  width = 'xwide',
  field = false,
  className,
  children,
}: {
  width?: 'narrow' | 'medium' | 'wide' | 'xwide'
  field?: boolean
  className?: string
  children: ReactNode
}) {
  const max = {
    narrow: 'max-w-[720px]',
    medium: 'max-w-[1024px]',
    wide: 'max-w-[1152px]',
    xwide: 'max-w-[1440px]',
  }[width]

  const column = (
    <div
      className={cn(
        'mx-auto flex w-full flex-col gap-[var(--stack-gap)] p-[var(--page-pad)]',
        max,
        className,
      )}
    >
      {children}
    </div>
  )

  return field ? <div className="min-h-full bg-ink-100">{column}</div> : column
}

export function Pagination({
  page,
  pageCount,
  total,
  onChange,
}: {
  page: number
  pageCount: number
  total: number
  onChange: (page: number) => void
}) {
  if (pageCount <= 1) {
    return (
      <div className="px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
        {total} {total === 1 ? 'result' : 'results'}
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 border-t border-ink-200 px-[var(--row-pad-x)] py-2">
      <p className="text-xs text-ink-500">
        Page <span className="tabular-nums">{page}</span> of{' '}
        <span className="tabular-nums">{pageCount}</span> ·{' '}
        <span className="tabular-nums">{total}</span> results
      </p>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          icon={<Icon name="back" className="size-4" />}
        >
          Previous
        </Button>
        <Button size="sm" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>
          Next
          <Icon name="forward" className="size-4" />
        </Button>
      </div>
    </div>
  )
}

/**
 * The top of a screen: what it is, one line about what is in it, and the
 * actions that belong to the whole page.
 *
 * On a phone the shell's own header already carries the title, so only the
 * description and the actions are drawn — they used to vanish with the title,
 * which is how "New client" and "New job" went missing on a handset.
 *
 * At a desk it is a `CommandBar`, title over description the way the job
 * card has the number over the client.
 */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** A small line above the title, for a record's number or its parent. */
  eyebrow?: ReactNode
}) {
  return (
    <CommandBar>
      <div className="min-w-0">
        {eyebrow ? (
          <div className="hidden text-xs font-medium text-brand-300/90 desk:block">{eyebrow}</div>
        ) : null}
        <CommandTitle className="hidden desk:block">{title}</CommandTitle>
        {description ? <CommandMeta>{description}</CommandMeta> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </CommandBar>
  )
}

/**
 * A row of mutually exclusive choices, drawn as the board's pill: Jobs' stage
 * tabs, the board's view switcher, a money screen's status.
 *
 * Selected is Charcoal, never green — this is "which one is on", not "do
 * this". On a command bar the pill turns over, white on Charcoal.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: ReadonlyArray<{ value: T; label: ReactNode; count?: number; title?: string }>
  onChange: (value: T) => void
  label: string
  className?: string
}) {
  const onCharcoal = useOnCharcoal()
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex w-full items-center gap-0.5 overflow-x-auto rounded-md p-0.5 sm:w-auto sm:shrink-0',
        onCharcoal ? 'bg-white/[0.08]' : 'bg-ink-100',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex min-h-[var(--control-sm)] flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs whitespace-nowrap transition-colors sm:flex-none',
              onCharcoal
                ? selected
                  ? 'bg-white font-semibold text-ink-900 shadow-sm'
                  : 'font-medium text-white/80 hover:bg-white/10 hover:text-white'
                : selected
                  ? 'bg-ink-900 font-semibold text-white shadow-sm'
                  : 'font-medium text-ink-600 hover:bg-white hover:text-ink-900',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span
                className={cn(
                  'text-[11px] tabular-nums',
                  selected
                    ? onCharcoal
                      ? 'text-ink-500'
                      : 'text-white/70'
                    : onCharcoal
                      ? 'text-white/50'
                      : 'text-ink-400',
                )}
              >
                {option.count.toLocaleString('en-AU')}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * A record's tabs, drawn as the job card's: bold Charcoal with an Eco Green
 * underline for the open one, and Charcoal counts. For the parts of one
 * record — a person's Details and Schedule, Settings' People and
 * Certifications. A choice of how a list reads is `Segmented`.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: ReadonlyArray<{ value: T; label: ReactNode; count?: number; title?: string }>
  onChange: (value: T) => void
  label: string
  className?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex items-center gap-1 overflow-x-auto border-b border-ink-200 px-[var(--row-pad-x)]',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex min-h-10 items-center gap-1.5 px-3 text-[13.5px] whitespace-nowrap transition-colors',
              selected
                ? 'font-bold text-ink-900 shadow-[inset_0_-3px_0_var(--color-brand-500)]'
                : 'font-semibold text-ink-500 hover:text-ink-900',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span
                className={cn(
                  'rounded-sm px-1.5 py-px text-[11px] font-bold tabular-nums',
                  selected ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-600',
                )}
              >
                {option.count.toLocaleString('en-AU')}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * A headline figure. The board's pulse tiles in miniature: an 11px label over
 * an 18px number with its caption beside it, so a strip of them is one short
 * band rather than a block the list starts under.
 *
 * Clickable when it filters the list below it, because a figure that says
 * "3 overdue" and does not take you to them is a dead end.
 */
export function StatTile({
  label,
  value,
  caption,
  tone,
  onClick,
  active,
  title,
}: {
  label: string
  value: ReactNode
  caption?: ReactNode
  /** Red is a problem, green is money in; anything else stays Charcoal. */
  tone?: 'red' | 'green'
  onClick?: () => void
  active?: boolean
  /** What the figure means, on hover. */
  title?: string
}) {
  const body = (
    <>
      <span className="block text-[11px] font-semibold tracking-wider text-ink-500 uppercase">
        {label}
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span
          className={cn(
            'font-display text-lg font-extrabold tabular-nums',
            tone === 'red' ? 'text-red-700' : tone === 'green' ? 'text-brand-700' : 'text-ink-900',
          )}
        >
          {value}
        </span>
        {caption ? <span className="min-w-0 text-xs text-ink-500">{caption}</span> : null}
      </span>
    </>
  )

  const base = 'min-w-0 flex-1 px-[var(--card-pad)] py-2 text-left'

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={cn(
        base,
        'transition-colors hover:bg-ink-50',
        active && 'bg-brand-50 shadow-[inset_0_-3px_0_var(--color-brand-500)] hover:bg-brand-50',
      )}
    >
      {body}
    </button>
  ) : (
    <div className={base} title={title}>
      {body}
    </div>
  )
}

/** Stat tiles in one card, divided by hairlines, wrapping to two up on a phone. */
export function StatStrip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Card className={cn('grid grid-cols-2 divide-ink-100 sm:flex sm:divide-x', className)}>
      {children}
    </Card>
  )
}

/**
 * Column names over a list. Shown from `sm` up, where the row is a grid with
 * the same template; a phone row stacks and needs no headings.
 */
export function ListHead({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'hidden items-center gap-3 border-b border-ink-100 bg-ink-50/60 px-[var(--row-pad-x)] py-1.5 text-[11px] font-semibold tracking-wider text-ink-500 uppercase sm:grid',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The shared row skin for a list. A grid from `sm` up, with the caller's
 * template; on a phone the cells flow and wrap, so a short cell sits beside
 * the one before it rather than taking a line of its own. A cell that should
 * own its line on a phone says `basis-full sm:basis-auto`.
 */
export const listRowClass =
  'flex flex-wrap items-center gap-x-2 gap-y-0.5 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50 sm:grid sm:gap-3'

/** A label and its value, in a record's details card. */
export function Detail({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-ink-900">{children}</dd>
    </div>
  )
}

/**
 * A labelled setting with its switch: the name, a line on what it does, and
 * `Toggle` at the end of the row. The switch's accessible name is the label,
 * so the visible text and what a screen reader says agree.
 */
export function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string
  description?: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="min-w-0 flex-1">
        <span className={cn('block text-sm font-medium', disabled ? 'text-ink-500' : 'text-ink-900')}>
          {label}
        </span>
        {description ? (
          <span className={cn('mt-px block text-xs', disabled ? 'text-ink-400' : 'text-ink-500')}>
            {description}
          </span>
        ) : null}
      </span>
      <Toggle label={label} checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  )
}
